import React from 'react';
import { View, StyleSheet, Platform } from 'react-native';
import { Card, useTheme } from 'react-native-paper';
import { useTranslation } from 'react-i18next';
import QText from './QText';
import { Button } from './index';

/**
 * ErrorBoundary Component
 *
 * Catches JavaScript errors anywhere in the child component tree and displays
 * a fallback UI instead of the component tree that crashed.
 *
 * Usage:
 * <ErrorBoundary
 *   onRefresh={() => window.location.reload()}
 *   onGoBack={() => navigation.goBack()}
 * >
 *   <YourComponent />
 * </ErrorBoundary>
 *
 * Props:
 * - onRefresh: Function to call when user clicks "Try Again"
 * - onGoBack: Function to call when user clicks "Go Back" (optional)
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error to console for debugging
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // You can also log the error to an error reporting service here
    this.setState({
      error: error,
      errorInfo: errorInfo,
    });
  }

  handleRefresh = () => {
    // Reset the error boundary state
    this.setState({ hasError: false, error: null, errorInfo: null });

    // If there's a refresh callback, call it
    if (this.props.onRefresh) {
      this.props.onRefresh();
    } else {
      // Default refresh behavior - reload the page for web
      if (Platform.OS === 'web') {
        window.location.reload();
      }
    }
  };

  handleGoBack = () => {
    // Reset the error boundary state
    this.setState({ hasError: false, error: null, errorInfo: null });

    // If there's a go back callback, call it
    if (this.props.onGoBack) {
      this.props.onGoBack();
    }
  };

  render() {
    if (this.state.hasError) {
      // Render fallback UI
      return (
        <ErrorFallbackScreen
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          onRefresh={this.handleRefresh}
          onGoBack={this.handleGoBack}
          showGoBack={!!this.props.onGoBack}
        />
      );
    }

    return this.props.children;
  }
}

// Functional component for the error fallback screen
const ErrorFallbackScreen = ({
  error,
  errorInfo,
  onRefresh,
  onGoBack,
  showGoBack,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const styles = createStyles(theme);

  return (
    <View style={styles.container}>
      <Card style={styles.errorCard}>
        <Card.Content style={styles.content}>
          {/* Error Icon */}
          <View style={styles.iconContainer}>
            <QText style={styles.errorIcon}>⚠️</QText>
          </View>

          {/* Error Title */}
          <QText style={styles.title}>{t('something_went_wrong')}</QText>

          {/* Error Message */}
          <QText style={styles.message}>
            {t('something_went_wrong_message')}
          </QText>

          {/* Error Details (only in development) */}
          {__DEV__ && error && (
            <View style={styles.errorDetails}>
              <QText style={styles.errorDetailsTitle}>
                {t('Error Details')} (Development Only):
              </QText>
              <QText style={styles.errorDetailsText}>{error.toString()}</QText>
            </View>
          )}

          {/* Action Buttons */}
          <View style={styles.buttonContainer}>
            <Button
              mode="contained"
              onPress={onRefresh}
              style={[styles.button, styles.refreshButton]}
            >
              {t('try_again')}
            </Button>

            {showGoBack && (
              <Button
                mode="outlined"
                onPress={onGoBack}
                style={[styles.button, styles.goBackButton]}
              >
                {t('go_back')}
              </Button>
            )}
          </View>
        </Card.Content>
      </Card>
    </View>
  );
};

const createStyles = (theme) =>
  StyleSheet.create({
    container: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: theme.colors.background,
      padding: 20,
    },
    errorCard: {
      width: '100%',
      maxWidth: 400,
      backgroundColor: theme.colors.surface,
      elevation: 4,
      borderRadius: 12,
    },
    content: {
      alignItems: 'center',
      paddingVertical: 32,
      paddingHorizontal: 24,
    },
    iconContainer: {
      marginBottom: 24,
    },
    errorIcon: {
      fontSize: 64,
      textAlign: 'center',
    },
    title: {
      fontSize: 20,
      fontWeight: 'bold',
      color: theme.colors.onSurface,
      textAlign: 'center',
      marginBottom: 16,
    },
    message: {
      fontSize: 16,
      color: theme.colors.onSurfaceVariant,
      textAlign: 'center',
      lineHeight: 24,
      marginBottom: 32,
    },
    errorDetails: {
      backgroundColor: theme.colors.errorContainer,
      padding: 16,
      borderRadius: 8,
      marginBottom: 24,
      width: '100%',
    },
    errorDetailsTitle: {
      fontSize: 14,
      fontWeight: 'bold',
      color: theme.colors.onErrorContainer,
      marginBottom: 8,
    },
    errorDetailsText: {
      fontSize: 12,
      color: theme.colors.onErrorContainer,
      fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
    },
    buttonContainer: {
      width: '100%',
      gap: 12,
    },
    button: {
      paddingVertical: 4,
    },
    refreshButton: {
      backgroundColor: theme.colors.primary,
    },
    goBackButton: {
      borderColor: theme.colors.outline,
    },
  });

export default ErrorBoundary;
